import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Card,
    CardContent,
    Backdrop,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    InputAdornment,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncIcon from "@mui/icons-material/Sync";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";

const AGENT_FAQS_URL = "/whatsapp-business/agentmanager/faqs";

const SORT_FIELDS = [
    // { value: "id", label: "ID" },
    // { value: "phrase", label: "Phrase" },
    // { value: "reply", label: "Reply" },
    // { value: "template_id", label: "Template ID" },
    // { value: "status", label: "Status" },
    { value: "created_at", label: "Created At" },
    { value: "updated_at", label: "Updated At" },
    { value: "processed_at", label: "Processed At" },
];

const toFaqPayload = (row) => ({
    question: String(row.question || row.phrase || "").trim(),
    answer: String(row.answer || row.reply || "").trim(),
    template_id:
        row.template_id != null && row.template_id !== ""
            ? String(row.template_id)
            : "",
    active: true,
});

const AutomationList = ({ refreshTrigger, onRowSynced }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
        from: 0,
        to: 0,
    });
    const [search, setSearch] = useState("");
    const [templateIdFilter, setTemplateIdFilter] = useState("");
    const [sortBy, setSortBy] = useState("id");
    const [sortOrder, setSortOrder] = useState("desc");
    const [statistics, setStatistics] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [syncingId, setSyncingId] = useState(null);
    const showNotification = useNotification();

    const pageRowIds = data.map((r) => r.id).filter((id) => id != null);
    const allPageSelected =
        pageRowIds.length > 0 && pageRowIds.every((id) => selectedIds.includes(id));
    const somePageSelected =
        pageRowIds.some((id) => selectedIds.includes(id)) && !allPageSelected;

    const fetchList = useCallback(
        async (page = 1) => {
            setLoading(true);
            try {
                const params = {
                    page,
                    per_page: 20,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                };
                if (search.trim()) params.search = search.trim();
                if (templateIdFilter !== "") {
                    if (templateIdFilter === "null") params.template_id = "null";
                    else params.template_id = templateIdFilter;
                }
                const response = await axiosInstance.get(
                    "/whatsapp-business/automation/list",
                    { params }
                );
                if (response.data?.success) {
                    setData(Array.isArray(response.data.data) ? response.data.data : []);
                    setPagination(
                        response.data.pagination || {
                            current_page: 1,
                            last_page: 1,
                            per_page: 20,
                            total: 0,
                            from: 0,
                            to: 0,
                        }
                    );
                    setStatistics(response.data.statistics || null);
                } else {
                    showNotification(response.data?.message || "Failed to load list", "error");
                }
            } catch (err) {
                const msg =
                    err.response?.data?.message || err.message || "Failed to load automation list";
                showNotification(msg, "error");
            } finally {
                setLoading(false);
            }
        },
        [search, templateIdFilter, sortBy, sortOrder]
    );

    useEffect(() => {
        fetchList(1);
    }, [fetchList, refreshTrigger]);

    const handlePageChange = (_, value) => {
        setSelectedIds([]);
        fetchList(value);
    };

    const toggleSelectOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAllPage = () => {
        if (allPageSelected) {
            setSelectedIds((prev) => prev.filter((id) => !pageRowIds.includes(id)));
        } else {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                pageRowIds.forEach((id) => next.add(id));
                return Array.from(next);
            });
        }
    };

    const openDeleteModal = () => {
        if (selectedIds.length === 0) return;
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        if (!bulkDeleting) setDeleteModalOpen(false);
    };

    const confirmBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        setBulkDeleting(true);
        try {
            const response = await axiosInstance.post(
                "/whatsapp-business/automation/delete",
                { ids: selectedIds }
            );
            if (response.data?.success) {
                showNotification(
                    response.data?.message || "Selected rows deleted",
                    "success"
                );
                setSelectedIds([]);
                setDeleteModalOpen(false);
                const page = pagination.current_page;
                await fetchList(page);
            } else {
                showNotification(
                    response.data?.message || "Failed to delete rows",
                    "error"
                );
            }
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.message ||
                "Failed to delete automation rows";
            showNotification(msg, "error");
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleRefresh = () => {
        fetchList(pagination.current_page);
    };

    const handleSyncRow = async (row) => {
        const faq = toFaqPayload(row);
        if (!faq.question || !faq.answer) {
            showNotification("Phrase and reply are required to sync", "warning");
            return;
        }
        setSyncingId(row.id);
        try {
            await axiosInstance.post(AGENT_FAQS_URL, { faqs: [faq] });
            showNotification("Synced with AI Agent", "success");
            if (typeof onRowSynced === "function") onRowSynced();
        } catch (err) {
            const msg =
                err.response?.data?.message || err.message || "Failed to sync with AI Agent";
            showNotification(msg, "error");
        } finally {
            setSyncingId(null);
        }
    };

    return (
        <Card variant="outlined" sx={{ overflow: "hidden" }}>
            <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
                    <Typography variant="h6">Automation records (phrase & reply)</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={openDeleteModal}
                            disabled={loading || selectedIds.length === 0}
                            sx={{ ...WB_SX.secondaryButton }}
                        >
                            Delete selected
                            {selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={loading}
                            sx={WB_SX.secondaryButton}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search phrase or reply..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && fetchList(1)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 220 }}
                    />
                    <TextField
                        size="small"
                        label="Template ID"
                        placeholder="e.g. 123 or leave empty"
                        value={templateIdFilter}
                        onChange={(e) => setTemplateIdFilter(e.target.value)}
                        sx={{ minWidth: 140 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Sort by</InputLabel>
                        <Select
                            value={sortBy}
                            label="Sort by"
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            {SORT_FIELDS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Order</InputLabel>
                        <Select
                            value={sortOrder}
                            label="Order"
                            onChange={(e) => setSortOrder(e.target.value)}
                        >
                            <MenuItem value="asc">Asc</MenuItem>
                            <MenuItem value="desc">Desc</MenuItem>
                        </Select>
                    </FormControl>
                    {/* <Button variant="contained" size="small" onClick={() => fetchList(1)}>
                        Apply
                    </Button> */}
                </Box>
                {statistics && (
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                        <Chip label={`Total: ${statistics.total_records ?? 0}`} size="small" />
                        {statistics.pending_count != null && (
                            <Chip
                                label={`Pending: ${statistics.pending_count}`}
                                size="small"
                                color="warning"
                            />
                        )}
                        {statistics.processed_count != null && (
                            <Chip
                                label={`Processed: ${statistics.processed_count}`}
                                size="small"
                                color="success"
                            />
                        )}
                    </Box>
                )}
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    padding="checkbox"
                                    sx={{ ...WB_SX.tableHeadCell, width: 48 }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={allPageSelected}
                                        indeterminate={somePageSelected}
                                        onChange={toggleSelectAllPage}
                                        disabled={loading || pageRowIds.length === 0}
                                        inputProps={{
                                            "aria-label": "Select all rows on this page",
                                        }}
                                        sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" } }}
                                    />
                                </TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}><strong>ID</strong></TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}><strong>Phrase</strong></TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}><strong>Reply</strong></TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}><strong>Template ID</strong></TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}><strong>Last Updated</strong></TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}><strong>Trigger Lead Automation</strong></TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}><strong>Sync</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && data.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        Loading…
                                    </TableCell>
                                </TableRow>
                            ) : data.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No records found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data.map((row) => (
                                    <TableRow key={row.id} sx={WB_SX.tableRow}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                checked={selectedIds.includes(row.id)}
                                                onChange={() => toggleSelectOne(row.id)}
                                                inputProps={{
                                                    "aria-label": `Select row ${row.id}`,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{row.id}</TableCell>
                                        <TableCell>{row.phrase ?? "—"}</TableCell>
                                        <TableCell>{row.reply ?? "—"}</TableCell>
                                        <TableCell>{row.template_id ?? "—"}</TableCell>
                                        <TableCell>
                                            {row.updated_at
                                                ? new Date(row.updated_at).toLocaleString()
                                                : "—"}
                                        </TableCell>
                                        <TableCell>
                                            0
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={
                                                    syncingId === row.id ? (
                                                        <CircularProgress size={14} color="inherit" />
                                                    ) : (
                                                        <SyncIcon />
                                                    )
                                                }
                                                onClick={() => handleSyncRow(row)}
                                                disabled={loading || syncingId != null}
                                                sx={{ ...WB_SX.secondaryButton, textTransform: "none" }}
                                            >
                                                {syncingId === row.id ? "Syncing…" : "Sync"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {pagination.last_page > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Pagination
                            count={pagination.last_page}
                            page={pagination.current_page}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}
            </CardContent>
            <Backdrop open={loading} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <Dialog
                open={deleteModalOpen}
                onClose={closeDeleteModal}
                maxWidth="sm"
                fullWidth
                aria-labelledby="automation-delete-dialog-title"
            >
                <DialogTitle id="automation-delete-dialog-title">
                    Delete automation rows?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to permanently delete{" "}
                        <strong>{selectedIds.length}</strong> automation record
                        {selectedIds.length === 1 ? "" : "s"}. This cannot be undone.
                    </DialogContentText>
                    {selectedIds.length <= 10 && (
                        <DialogContentText sx={{ mt: 1, fontFamily: "monospace", fontSize: "0.8rem" }}>
                            IDs: {selectedIds.join(", ")}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDeleteModal} disabled={bulkDeleting} sx={WB_SX.secondaryButton}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmBulkDelete}
                        color="error"
                        variant="contained"
                        disabled={bulkDeleting}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: WB_UI.radius.button,
                            px: 3,
                        }}
                    >
                        {bulkDeleting ? "Deleting…" : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default AutomationList;
